import { AttributeDefinitionDc, AttributeType } from "@evergis/api";
import { Dropdown, IconTypesKeys, IJSXOption } from "@evergis/ui";
import React, { FC, useCallback, useMemo } from "react";
import { getFieldTypeIcon } from "../../../evergis/core/attributes/fieldTypes";
import { LayerInfo, TableInfo } from "../../../interfaces/resourceInfo";
import { SelectContainer } from "./styled";

export const extractLayerAttributesToSelectOptions = (
  layer: LayerInfo,
  excludeAttributes: string[] = [],
  allowedTypes?: AttributeType[],
): IJSXOption[] => {
  return (
    layer &&
    layer.layerDefinition &&
    formatAttributesConfigurationToOptions(layer.layerDefinition.attributes, excludeAttributes, allowedTypes)
  );
};

export const formatAttributesConfigurationToOptions = (
  attributes: Record<string, AttributeDefinitionDc>,
  excludeAttributes: string[] = [],
  allowedTypes?: AttributeType[],
): IJSXOption[] =>
  Object.entries(attributes)
    .filter(
      ([attributeName, { isCalculated, type }]) =>
        !excludeAttributes.includes(attributeName) &&
        !isCalculated &&
        (allowedTypes ? allowedTypes.includes(type) : true),
    )
    .map(([attributeName, { alias, type, subType }]) => ({
      value: attributeName,
      text: alias || attributeName,
      id: attributeName,
      icon: getFieldTypeIcon(type, subType) as IconTypesKeys,
    }));

export const extractTableColumnsToSelectOptions = (
  table: TableInfo,
  excludeAttributes: string[] = [],
  allowedTypes?: AttributeType[],
): IJSXOption[] => {
  return (
    table &&
    table.columns &&
    table.columns
      .filter(
        ({ name, type }) => !excludeAttributes.includes(name) && (allowedTypes ? allowedTypes.includes(type) : true),
      )
      .map(({ name, type }) => ({
        value: name,
        text: name,
        id: name,
        icon: getFieldTypeIcon(type) as IconTypesKeys,
      }))
  );
};

export type AttributeSelectorProps = {
  layer?: LayerInfo;
  table?: TableInfo;
  attribute: string;
  label?: string;
  placeholder?: string;
  width?: string;
  selectAttribute: (attribute: string) => void;
  loading?: boolean;
  excludeAttributes?: string[];
  className?: string;
  zIndex?: number;
  disabled?: boolean;
  help?: React.ReactNode;
  types?: AttributeType[];
};

const DEFAULT_POPUP_Z_INDEX = 120;

export const AttributeSelector: FC<AttributeSelectorProps> = ({
  layer,
  table,
  label,
  placeholder,
  attribute,
  selectAttribute,
  excludeAttributes,
  width,
  loading,
  className,
  zIndex = DEFAULT_POPUP_Z_INDEX,
  disabled,
  help,
  types,
}) => {
  const onChange = useCallback(option => selectAttribute(option[0].value), [selectAttribute]);
  const options = useMemo(
    () =>
      layer
        ? extractLayerAttributesToSelectOptions(layer, excludeAttributes, types)
        : extractTableColumnsToSelectOptions(table, excludeAttributes, types),
    [layer, table, excludeAttributes, types],
  );

  return (
    <SelectContainer className={className}>
      <Dropdown
        disabled={disabled || loading || !(layer || table)}
        options={options}
        width={width || "17.25rem"}
        value={attribute}
        onChange={onChange}
        label={label}
        placeholder={placeholder}
        zIndex={zIndex}
        help={help}
      />
    </SelectContainer>
  );
};
